import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Zap, Battery, Clock, BarChart3, TrendingUp, Play, Square } from 'lucide-react';
import { formatNumber } from '@/lib/utils';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import type { ChargerSystem } from '@/types';

interface ChargerPanelProps {
  system: ChargerSystem;
  onUpdate?: (settings: Partial<ChargerSystem>) => void;
}

type TimePeriod = '7d' | '30d' | '90d';

export function ChargerPanel({ system, onUpdate }: ChargerPanelProps) {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('7d');

  const getStatusColor = (status: string) => {
    if (status === 'charging') return 'success';
    if (status === 'idle') return 'secondary';
    return 'error';
  };

  const getStatusLabel = (status: string) => {
    if (status === 'charging') return 'Charging';
    if (status === 'idle') return 'Ready';
    return 'Disconnected';
  };

  // Energy delivered per day, aggregated from REAL charging sessions.
  //
  // This used to be `Math.random() * 50 + 10` per day — a fully invented bar
  // chart rendered identically to measured data, for an integration that is
  // still a documented placeholder. With no sessions recorded the chart shows
  // an empty state instead of inventing a plausible history.
  const chartData = useMemo(() => {
    const days = timePeriod === '7d' ? 7 : timePeriod === '30d' ? 30 : 90;
    const labels: string[] = [];
    const data: number[] = [];

    // kWh per calendar day. Bucketed in the VIEWER's timezone, not UTC: the bars
    // are labelled with toLocaleDateString(), so keying on toISOString() would
    // file a session under the neighbouring day's label either side of the UTC
    // boundary — and could push a boundary session outside the window entirely.
    const dayKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const kwhByDay = new Map<string, number>();
    let sessionsInWindow = false;
    for (const session of system.history ?? []) {
      const day = dayKey(new Date(session.timestamp));
      const kwh = (session.powerDelivered || 0) * ((session.duration || 0) / 60);
      kwhByDay.set(day, (kwhByDay.get(day) ?? 0) + kwh);
    }

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);

      if (days === 7) {
        labels.push(date.toLocaleDateString('en-US', { weekday: 'short' }));
      } else if (days === 30) {
        labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      } else {
        labels.push(i % 7 === 0 ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
      }

      const kwh = kwhByDay.get(dayKey(date));
      if (kwh != null) sessionsInWindow = true;
      data.push(kwh ?? 0);
    }

    // Scoped to the SELECTED window, not all history: sessions that exist only
    // outside the chosen range would otherwise render a chart of all-zero bars
    // instead of the empty state.
    return { labels, data, hasSessions: sessionsInWindow };
  }, [timePeriod, system.history]);

  // Memoize ApexCharts options and series to prevent re-creating on every render.
  // Unstable references cause ApexCharts to call updateOptions/updateSeries internally,
  // which triggers SVG path re-parsing and animation that can fail with "parser Error".
  const barChartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: 'bar' as const,
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { dynamicAnimation: { enabled: false } },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: timePeriod === '7d' ? '60%' : timePeriod === '30d' ? '80%' : '90%',
      },
    },
    colors: ['#3B82F6'], // info blue
    theme: { mode: 'dark' },
    grid: {
      borderColor: '#374151',
      strokeDashArray: 3,
      padding: { left: 0, right: 0 },
    },
    xaxis: {
      categories: chartData.labels,
      labels: {
        show: timePeriod !== '90d',
        style: { colors: '#9CA3AF', fontSize: '10px' },
        rotate: timePeriod === '30d' ? -45 : 0,
        rotateAlways: timePeriod === '30d',
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9CA3AF', fontSize: '10px' },
        formatter: (val: number) => `${formatNumber(val, 0)}`,
      },
    },
    tooltip: {
      theme: 'dark',
      y: { formatter: (val: number) => `${formatNumber(val, 1)} kWh` },
    },
    dataLabels: { enabled: false },
  }), [timePeriod, chartData.labels]);

  const barChartSeries = useMemo(
    () => [{ name: 'Energy Delivered', data: chartData.data }],
    [chartData.data]
  );

  // 12-hour power history from REAL sessions only. Previously
  // `Math.random() * 8 + 2` per hour, presented as measured power draw.
  const powerHistory = useMemo(() => {
    const cutoff = Date.now() - 12 * 3600000;
    return (system.history ?? [])
      .filter(session => new Date(session.timestamp).getTime() >= cutoff)
      .map(session => ({ timestamp: session.timestamp, power: session.powerDelivered }));
  }, [system.history]);


  const { timeLabels, powerData } = useMemo(() => ({
    timeLabels: powerHistory.map(h =>
      new Date(h.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    ),
    powerData: powerHistory.map(h => h.power),
  }), [powerHistory]);

  // Memoize ApexCharts options for 12-hour power area chart
  const powerChartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: 'area' as const,
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
      sparkline: { enabled: false },
      animations: { dynamicAnimation: { enabled: false } },
    },
    stroke: { curve: 'smooth', width: 2 },
    colors: ['#3B82F6'],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] },
    },
    theme: { mode: 'dark' },
    grid: { borderColor: '#374151', strokeDashArray: 3 },
    xaxis: {
      categories: timeLabels,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9CA3AF', fontSize: '10px' },
        formatter: (val: number) => `${formatNumber(val, 1)}`,
      },
    },
    tooltip: {
      theme: 'dark',
      y: { formatter: (val: number) => `${formatNumber(val, 2)} kW` },
    },
    dataLabels: { enabled: false },
  }), [timeLabels]);

  const powerChartSeries = useMemo(
    () => [{ name: 'Power', data: powerData }],
    [powerData]
  );

  // Calculate efficiency percentage for circular indicator
  const efficiency = system.powerIn > 0 ? (system.powerOut / system.powerIn) * 100 : 0;

  return (
    <Card className="flex flex-col mb-4 lg:mb-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-info" />
            <CardTitle>Tesla Charger</CardTitle>
          </div>
          <Badge variant={getStatusColor(system.status)}>
            {getStatusLabel(system.status)}
          </Badge>
        </div>
        <CardDescription>EV charging station</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 flex-1">
        {/* Power Flow + Efficiency + Controls - All on same row */}
        <div className="grid grid-cols-4 gap-3">
          {/* Power In */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Zap className="h-3 w-3 rotate-180" />
              <span>Power In</span>
            </div>
            <div className="text-xl font-bold text-info">
              {formatNumber(system.powerIn)} kW
            </div>
            {system.voltage !== undefined && (
              <div className="text-xs text-muted-foreground">
                {formatNumber(system.voltage)} V
              </div>
            )}
          </div>

          {/* Power Out */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Zap className="h-3 w-3" />
              <span>Power Out</span>
            </div>
            <div className="text-xl font-bold">
              {formatNumber(system.powerOut)} kW
            </div>
            {system.current !== undefined && (
              <div className="text-xs text-muted-foreground">
                {formatNumber(system.current)} A
              </div>
            )}
          </div>

          {/* Charging Efficiency */}
          <div className="flex flex-col items-center">
            <div className="text-xs text-muted-foreground mb-1">Efficiency</div>
            <div className="relative w-14 h-14">
              <svg className="w-14 h-14 transform -rotate-90">
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="text-muted/30"
                />
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={2 * Math.PI * 24}
                  strokeDashoffset={2 * Math.PI * 24 - (efficiency / 100) * 2 * Math.PI * 24}
                  className="text-success transition-all duration-300"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-sm font-bold text-success">
                  {formatNumber(efficiency, 0)}%
                </div>
              </div>
            </div>
          </div>

          {/* Charger Controls - Large Square Buttons */}
          <div className="flex flex-col items-center">
            <div className="text-xs text-muted-foreground mb-1">Control</div>
            <div className="flex gap-2">
              <Button
                variant={system.status === 'charging' ? 'default' : 'outline'}
                onClick={() => onUpdate?.({ status: 'charging' })}
                disabled={system.status === 'disconnected'}
                className="h-14 w-14"
                title="Start Charging"
              >
                <Play className="h-5 w-5" />
              </Button>
              <Button
                variant={system.status === 'idle' ? 'default' : 'outline'}
                onClick={() => onUpdate?.({ status: 'idle' })}
                disabled={system.status === 'disconnected'}
                className="h-14 w-14"
                title="Stop Charging"
              >
                <Square className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Battery Level (if connected) */}
        {system.batteryLevel !== undefined && (
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Battery className="h-5 w-5 text-info" />
                <span className="text-sm font-medium">Vehicle Battery</span>
              </div>
              <span className="text-2xl font-bold text-info">{system.batteryLevel}%</span>
            </div>
            <div className="relative h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-info rounded-full transition-all"
                style={{ width: `${system.batteryLevel}%` }}
              />
            </div>
          </div>
        )}

        {/* Time Remaining & Session Duration (if charging) */}
        {system.status === 'charging' && (system.timeRemaining !== undefined || system.sessionDuration !== undefined) && (
          <div className="flex items-center justify-between pt-4 border-t border-border">
            {system.timeRemaining !== undefined && (
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>Time Remaining</span>
                </div>
                <span className="text-lg font-bold">
                  {Math.floor(system.timeRemaining / 60)}h {system.timeRemaining % 60}m
                </span>
              </div>
            )}
            {system.sessionDuration !== undefined && (
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Session Duration</div>
                <span className="text-lg font-bold">
                  {Math.floor(system.sessionDuration / 60)}h {system.sessionDuration % 60}m
                </span>
              </div>
            )}
          </div>
        )}

        {/* 12-Hour Power Delivery Area Chart */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">12-Hour Power Delivery</span>
          </div>
          <div className="relative h-[160px] bg-muted/30 rounded-lg">
            {powerHistory.length > 0 ? (
              <>
                <Chart
                  type="area"
                  options={powerChartOptions}
                  series={powerChartSeries}
                  height={160}
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground px-3 -mt-2">
                  <span>12h ago</span>
                  <span>Now</span>
                </div>
              </>
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-xs text-muted-foreground">No charging activity in the last 12 hours</span>
              </div>
            )}
          </div>
        </div>

        {/* Energy Delivery Bar Chart with Time Period Picker */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Energy Delivered</span>
            </div>
            <Select value={timePeriod} onValueChange={(v) => setTimePeriod(v as TimePeriod)}>
              <SelectTrigger className="w-[100px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 Days</SelectItem>
                <SelectItem value="30d">30 Days</SelectItem>
                <SelectItem value="90d">90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="h-[180px] bg-muted/30 rounded-lg">
            {chartData.hasSessions ? (
              <Chart
                type="bar"
                options={barChartOptions}
                series={barChartSeries}
                height={180}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-xs text-muted-foreground">No charging sessions recorded</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
